import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Chip,
  Collapse,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Popper,
  ClickAwayListener
} from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SearchIcon from '@mui/icons-material/Search';
import TemplateIcon from '@mui/icons-material/LibraryBooks';
import BarcodeScanner from './BarcodeScanner';

const ReceiveBarcodeStep = ({
  formData,
  setFormData,
  onBarcodeChange,
  isAutoFilled,
  scanResult,
  searchSuggestions = [],
  showSuggestions = false,
  searchLoading = false,
  onSuggestionSelect = () => {}
}) => {
  const [showScanner, setShowScanner] = useState(false); // Changed to false - hidden by default
  const barcodeInputRef = useRef(null);

  // Auto-focus the barcode input field on component mount
  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  // Keep focus on barcode input when camera is toggled
  useEffect(() => {
    // Small delay to ensure DOM updates are complete
    const timer = setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [showScanner]); // Re-focus whenever camera is shown/hidden

  const handleScanSuccess = (code) => {
    setFormData(prev => ({ ...prev, barcode: code }));
    onBarcodeChange(code);
  };

  const handleBarcodeChange = (e) => {
    const newBarcode = e.target.value;
    setFormData(prev => ({ ...prev, barcode: newBarcode }));
  };

  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter' && formData.barcode) {
      onBarcodeChange(formData.barcode);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.primary' }}>
        Step 1: Scan or Enter Barcode
      </Typography>
      
      {/* Scanner Toggle - Only show if user wants to use camera */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          🔍 Using external barcode scanner
        </Typography>
        <Tooltip title={showScanner ? "Hide camera scanner" : "Use laptop camera instead"}>
          <IconButton 
            color={showScanner ? "secondary" : "default"}
            onClick={() => setShowScanner(!showScanner)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            {showScanner ? "Hide Camera" : "📷 Use Camera"}
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Barcode Input Field - Always visible for external scanner */}
      <Box sx={{ mb: 2, position: 'relative' }}>
        <ClickAwayListener onClickAway={() => showSuggestions && onSuggestionSelect(null)}>
          <Box>
            <TextField
              label="Equipment Barcode or Search"
              fullWidth
              value={formData.barcode || ''}
              onChange={handleBarcodeChange}
              onKeyPress={handleBarcodeKeyPress}
              placeholder="Scan barcode, or type manufacturer, model, or description"
              inputRef={barcodeInputRef}
              autoFocus
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SearchIcon color="action" />
                    )}
                  </InputAdornment>
                )
              }}
              helperText="Scan with barcode scanner, or search by typing manufacturer, model, or description"
            />

            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflow: 'auto'
                }}
              >
                <List dense>
                  {searchSuggestions.map((suggestion, index) => (
                    <ListItem
                      key={`${suggestion.type}-${suggestion.id}`}
                      button
                      onClick={() => onSuggestionSelect(suggestion)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <TemplateIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={suggestion.title}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {suggestion.subtitle}
                            </Typography>
                            {suggestion.barcode && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                • {suggestion.barcode}
                              </Typography>
                            )}
                            <Chip
                              label="Template"
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ ml: 1, height: 20 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </ClickAwayListener>
      </Box>

      {/* Camera Scanner Component - Only shown when requested */}
      <Collapse in={showScanner}>
        {showScanner && (
          <Box sx={{ 
            position: 'relative',
            height: '400px',
            maxHeight: '400px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#f5f5f5',
            mb: 2
          }}>
            <BarcodeScanner 
              onScanSuccess={handleScanSuccess}
              onError={(err) => {
                // Only log actual errors, not "not found" which happens constantly
                if (!err.message?.includes('NotFound') && !err.message?.includes('parse error')) {
                  console.error('Scan error:', err);
                }
              }}
              width="100%"
              autoStart={showScanner}
              continuous={false}
              height={350}
            />
          </Box>
        )}
      </Collapse>

    </Box>
  );
};

export default ReceiveBarcodeStep;